/**
 * Código de estadía AF — identificador único y universal de un arriendo/estadía.
 *
 * Es la PK numérica del modelo Booking (`reservas.id`). Se asigna solo (auto-
 * incremental de la DB), es ESTABLE (no cambia aunque se editen las fechas: los
 * PATCH del calendario/recalc hacen `update()` sobre la misma fila) y es único.
 *
 * Sirve como identificador común para hacer seguimientos y búsquedas en todo el
 * sistema: se muestra como «Estadía #N» en calendario, arriendos, aseos y en las
 * vistas del cliente. El aseo vinculado lo replica vía `aseos.reserva_id`, así el
 * mismo #N cruza un arriendo con su aseo aunque caigan en días distintos.
 *
 * NO confundir con:
 *   - `id_reservac`  → «Ref. plataforma» = código corto de la plataforma (Airbnb/Booking/Stays, tipo MJ23J). Solo cuando la reserva vino de una plataforma.
 *   - `id_reserval`  → ID interno de Stays.
 *   - `numero_corto` → AF-YYYYMMDD-NN. LEGACY / congelado (round 2026-07-20): ya no se genera ni se muestra; lo reemplaza «Estadía #N».
 *
 * Fuente única del formato: usar `formatEstadiaCode()` / `<EstadiaCodeTag>` en
 * cualquier superficie nueva que muestre el código, y `parseEstadiaQuery()` para
 * interpretar lo que el usuario escribe en un buscador. Ver docs/glosario.md.
 */

/** Label completo — títulos de columna, labels de filtro, cabeceras de XLS. */
export const ESTADIA_LABEL = "Código de estadía AF";

/** Texto corto del Tag / prefijo humano. */
export const ESTADIA_SHORT = "Estadía";

/** Tooltip explicativo canónico (se muestra una vez por superficie). */
export const ESTADIA_TOOLTIP =
  "Número interno único de esta estadía. Su aseo vinculado lleva esta misma " +
  "etiqueta «Estadía #N», así los cruzas aunque estén en días distintos. " +
  "No cambia aunque edites las fechas.";

/**
 * Formatea el código para mostrar: `#123`. Devuelve `—` si es null/undefined/"".
 * Es SOLO presentación — no usar en value/key de Select, búsqueda ni exportación
 * cruda (ahí va el número pelado).
 */
export function formatEstadiaCode(id?: number | string | null): string {
  if (id == null || id === "") return "—";
  return `#${id}`;
}

/**
 * Normaliza una consulta de usuario ("#1472", " 1472 ", "1472") al número puro
 * de dígitos. Devuelve "" si, tras quitar el `#` y los espacios, no queda un
 * entero puro (así un buscador puede decidir si tratar el término como código
 * de estadía o como texto libre).
 */
export function parseEstadiaQuery(q: string | null | undefined): string {
  const t = String(q ?? "").trim().replace(/^#/, "").trim();
  return /^\d+$/.test(t) ? t : "";
}

/* ─── Código de aseo (aseos.id) ───────────────────────────────────────────────
 * Cada aseo tiene su PROPIO código único = su PK autoincremental (`aseos.id`),
 * distinto del «Estadía #N» del arriendo vinculado. Mismo formato "#N"
 * (`formatEstadiaCode` sirve para ambos) e IGUAL garantía histórica: la
 * secuencia solo avanza, un número no se reasigna jamás aunque se borren aseos
 * (guard de no-regresión en backend/src/bootstrap/schema.bootstrap.js). Se
 * muestra como «Aseo #N» vía <AseoCodeTag>. Ver docs/glosario.md.
 */

/** Label completo del código de aseo — títulos de columna, headers. */
export const ASEO_LABEL = "Código de aseo";

/** Texto corto del Tag / prefijo humano del código de aseo. */
export const ASEO_SHORT = "Aseo";

/** Tooltip explicativo canónico del código de aseo. */
export const ASEO_TOOLTIP =
  "Número interno único de este aseo. No se repite jamás, aunque se borren " +
  "aseos (la secuencia solo avanza). Es distinto del «Estadía #N», que " +
  "identifica el arriendo vinculado.";
